/**
 * Service Worker registration and update detection
 *
 * Registers the SW only in production, detects new versions,
 * and notifies the app to show the update bar.
 */

export interface SWUpdateCallback {
  (newVersion: string): void;
}

let registration: ServiceWorkerRegistration | null = null;

/**
 * Register the service worker and listen for updates.
 * Only registers in production builds.
 *
 * @param onUpdateAvailable - callback when a new version is available
 * @returns Promise that resolves after SW is registered
 */
export async function registerServiceWorker(
  onUpdateAvailable: SWUpdateCallback
): Promise<void> {
  // Only register in production
  if (process.env.NODE_ENV !== 'production') {
    console.log('[SW] Skipping SW registration in development');
    return;
  }

  // Check if SW is supported
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service Workers not supported');
    return;
  }

  try {
    // Construct SW path at runtime to avoid Parcel resolution issues with public assets
    const swPath = `${window.location.origin.replace(/\/$/, '')}/sw.js`;
    registration = await navigator.serviceWorker.register(swPath, {
      scope: '/'
    });

    console.log('[SW] Service Worker registered');

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration!.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New SW is installed and there's an active controller (update available)
          getWaitingWorkerVersion(newWorker).then((newVersion) => {
            console.log(`[SW] Update available: v${newVersion}`);
            onUpdateAvailable(newVersion);
          });
        }
      });
    });
  } catch (err) {
    console.error('[SW] Registration failed:', err);
  }
}

/**
 * Get version from a waiting service worker via postMessage
 */
async function getWaitingWorkerVersion(worker: ServiceWorker): Promise<string> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();

    const timeout = setTimeout(() => {
      console.warn('[SW] Version query timeout, using fallback');
      resolve('available');
    }, 2000);

    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      const version = event.data?.version || 'available';
      resolve(version);
    };

    // Send version request to waiting worker
    worker.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
  });
}

/**
 * Skip the waiting phase and activate the new SW immediately.
 * The app will reload after activation.
 */
export function skipWaiting(): void {
  if (!registration?.waiting) {
    console.warn('[SW] No waiting SW to activate');
    return;
  }

  // Tell the waiting SW to skip waiting and take control
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });

  // Once the new SW activates, reload the page
  let updateCheckInterval: NodeJS.Timeout | null = null;

  const checkController = () => {
    if (navigator.serviceWorker.controller?.state === 'activated') {
      console.log('[SW] New SW activated, reloading...');
      if (updateCheckInterval) clearInterval(updateCheckInterval);
      window.location.reload();
    }
  };

  // Poll for controller change (SW activation)
  updateCheckInterval = setInterval(checkController, 100);

  // Timeout after 5 seconds
  setTimeout(() => {
    if (updateCheckInterval) {
      clearInterval(updateCheckInterval);
      window.location.reload(); // Reload anyway
    }
  }, 5000);
}

/**
 * Handle SW messages (e.g., emergency cleanup notifications)
 */
export function listenToSWMessages(
  onMessage: (event: Event) => void
): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', onMessage);
  }
}
